'use client'

import { memo, type FC } from 'react'

import {
  getAmountField,
  getEditableFields,
  isRowOverMax,
  type ChecklistOption,
  type ChecklistRow,
} from '../lib/checklistHelper'
import { parseChecklistOption } from '../lib/checklistOptionDetails'
import { CheckboxBlock, cn, CopyButton, Input, Label } from '@fp/ui-kit'

const AMOUNT_STEP = 0.01
const UUID_HEAD_LENGTH = 8

const getMethodClasses = (paymentMethod?: string) => {
  switch (paymentMethod) {
    case 'deposit_refund':
      return { bar: 'border-l-success', kind: 'text-success-foreground', tint: 'bg-success/10', detail: 'text-primary' }
    case 'crypto':
      return { bar: 'border-l-chart-2', kind: 'text-chart-2', tint: 'bg-chart-2/10', detail: 'text-primary' }
    case 'wire_transfer':
      return { bar: 'border-l-primary', kind: 'text-primary', tint: 'bg-primary/10', detail: 'text-primary' }
    default:
      return {
        bar: 'border-l-muted-foreground',
        kind: 'text-muted-foreground',
        tint: 'bg-muted',
        detail: 'text-xs text-muted-foreground',
      }
  }
}

type ChecklistOptionRowProps = {
  option: ChecklistOption
  row: ChecklistRow
  rowId: string
  index: number
  onToggle: (index: number, checked: boolean) => void
  onValueChange: (index: number, paramKey: string, value: string) => void
}

export const ChecklistOptionRow: FC<ChecklistOptionRowProps> = memo(
  ({ option, row, rowId, index, onToggle, onValueChange }) => {
    const checkboxId = `${rowId}-checkbox`
    const amountField = getAmountField(option)
    const extraFields = getEditableFields(option).filter((f) => f !== amountField)
    const { kind, paymentMethod, title, refundable, details, date, time, uuid } = parseChecklistOption(option)
    const methodClasses = getMethodClasses(paymentMethod)

    return (
      <div
        className={cn(
          'grid gap-0.5 border-l-[3px] px-3 py-1.5 transition-colors',
          methodClasses.bar,
          row.checked ? methodClasses.tint : 'hover:bg-muted/50',
        )}
      >
        <div className='flex items-center gap-2'>
          <CheckboxBlock id={checkboxId} checked={row.checked} onCheckedChange={(c) => onToggle(index, !!c)} />

          <Label htmlFor={checkboxId} className='grid min-w-0 flex-1 cursor-pointer text-sm font-normal'>
            <span className='flex flex-wrap items-baseline gap-x-2'>
              <span className={cn('text-xs font-bold uppercase tracking-wide', methodClasses.kind)}>{kind}</span>
              {title.length > 0 && <span className='font-medium text-foreground'>{title.join(' · ')}</span>}
              {refundable && (
                <span className='tabular-nums'>
                  <span className='font-semibold text-success-foreground'>{refundable}</span>
                  <span className='text-xs text-muted-foreground'> refundable</span>
                </span>
              )}
            </span>

            {(details.length > 0 || date) && (
              <span className='flex flex-wrap items-baseline gap-x-2'>
                {details.length > 0 && <span className={methodClasses.detail}>{details.join(' · ')}</span>}
                {date && (
                  <span className='text-xs text-muted-foreground tabular-nums'>
                    {date}
                    {time && <span className='ml-1 opacity-70'>{time}</span>}
                  </span>
                )}
              </span>
            )}
          </Label>

          {amountField && (
            <Input
              type='number'
              step={AMOUNT_STEP}
              min={0}
              aria-label={amountField.name || 'Amount'}
              value={row.values[amountField.param_key] ?? ''}
              disabled={!row.checked}
              onChange={(e) => onValueChange(index, amountField.param_key, e.target.value || '0')}
              className={cn(
                'h-8 w-24 shrink-0 text-right text-sm font-medium tabular-nums',
                isRowOverMax(option, row) && 'border-destructive-foreground focus-visible:ring-destructive-foreground',
              )}
            />
          )}
        </div>

        {uuid && (
          <div className='flex items-center pl-6'>
            <span className='select-all font-mono text-[13px] text-foreground/70'>
              <span className='font-semibold text-foreground'>{uuid.slice(0, UUID_HEAD_LENGTH)}</span>
              {uuid.slice(UUID_HEAD_LENGTH)}
            </span>
            <CopyButton value={uuid} aria-label='Copy UUID' className='h-5 px-1 text-muted-foreground' />
          </div>
        )}

        {extraFields.map((extraField) => (
          <div key={extraField.param_key} className='grid gap-1 pl-6'>
            <Label htmlFor={`${rowId}-${extraField.param_key}`} className='text-xs text-muted-foreground'>
              {extraField.name || extraField.param_key}
            </Label>
            <Input
              id={`${rowId}-${extraField.param_key}`}
              type={extraField.type === 'number' ? 'number' : 'text'}
              value={row.values[extraField.param_key] ?? ''}
              disabled={!row.checked}
              onChange={(e) => onValueChange(index, extraField.param_key, e.target.value)}
            />
          </div>
        ))}
      </div>
    )
  },
)
ChecklistOptionRow.displayName = 'ChecklistOptionRow'
